/**
 * @fileoverview useLenis.js
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import { useEffect } from 'react'
import Lenis from 'lenis'

/**
 * useLenis - initialises the Lenis smooth-scroll engine for the entire page.
 * Call this once at the top of the component tree (App.jsx).
 *
 * Lenis hijacks the native scroll, adds momentum / easing, and RAF-syncs
 * with the browser so it stays perfectly smooth on 60 / 120 hz displays.
 */
export function useLenis() {
    useEffect(() => {
        const lenis = new Lenis({
            duration: 1.2,          // scroll animation length in seconds
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // expo ease-out
            orientation: 'vertical',
            gestureOrientation: 'vertical',
            smoothWheel: true,
            wheelMultiplier: 1,
            touchMultiplier: 2,
        });

        // Hook into the RAF loop
        function raf(time) {
            lenis.raf(time);
            requestAnimationFrame(raf);
        }

        const rafId = requestAnimationFrame(raf);

        // Expose lenis globally so external code can call lenis.scrollTo()
        window.__lenis = lenis;

        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
            delete window.__lenis;
        };
    }, []);
}
